import { ChevronLeft, ChevronRight, CheckCircle2, Clock, Calendar, Sparkles } from "lucide-react";
import { MONTH_NAMES } from "@/lib/monthlyObjectives";

const SHORT_MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];

export default function MonthlyTimelineNav({
  year,
  selectedMonth,
  onSelectMonth,
  monthsData = [],
  currentState,
}) {
  const now = new Date();
  const currentCalYear = now.getFullYear();
  const currentCalMonth = now.getMonth() + 1;
  const isCurrentYear = year === currentCalYear;

  const handlePrev = () => {
    if (selectedMonth > 1) onSelectMonth(selectedMonth - 1);
  };

  const handleNext = () => {
    if (selectedMonth < 12) onSelectMonth(selectedMonth + 1);
  };

  const handleJumpToCurrent = () => {
    if (isCurrentYear) {
      onSelectMonth(currentCalMonth);
    } else if (year < currentCalYear) {
      onSelectMonth(12);
    } else {
      onSelectMonth(1);
    }
  };

  return (
    <div className="space-y-3" data-testid="monthly-timeline-navigation">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Frise temporelle des 12 mois
          </span>
          <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-slate-300">
            {year}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {isCurrentYear && (
            <button
              type="button"
              onClick={handleJumpToCurrent}
              className="inline-flex items-center gap-1.5 rounded-lg border border-lenvia-blue/30 bg-lenvia-blue/10 px-2.5 py-1 text-xs font-medium text-lenvia-blue transition hover:bg-lenvia-blue/20"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>Mois en cours ({SHORT_MONTHS[currentCalMonth - 1]})</span>
            </button>
          )}

          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-slate-950/60 p-0.5">
            <button
              type="button"
              onClick={handlePrev}
              disabled={selectedMonth <= 1}
              aria-label="Mois précédent"
              className="flex h-7 w-7 items-center justify-center rounded text-slate-400 transition hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-1 text-xs font-medium text-slate-300">
              {String(selectedMonth).padStart(2, "0")} / 12
            </span>
            <button
              type="button"
              onClick={handleNext}
              disabled={selectedMonth >= 12}
              aria-label="Mois suivant"
              className="flex h-7 w-7 items-center justify-center rounded text-slate-400 transition hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        role="group"
        aria-label="Mois à consulter"
        className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12"
      >
        {MONTH_NAMES.map((name, index) => {
          const monthNum = index + 1;
          const isSelected = selectedMonth === monthNum;
          const isCurrent = isCurrentYear && currentCalMonth === monthNum;
          const info = monthsData.find((item) => item.month === monthNum);
          const isClosed = info?.closed ?? false;
          const isConfigured = info?.configured ?? false;

          let badgeColor = "text-slate-400 bg-slate-800/60 border-slate-700/50";
          let badgeText = "À réaliser";
          let IconComponent = Calendar;

          if (isClosed) {
            badgeColor = "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
            badgeText = "Clôturé";
            IconComponent = CheckCircle2;
          } else if (isCurrent) {
            badgeColor = "text-lenvia-blue bg-lenvia-blue/10 border-lenvia-blue/30";
            badgeText = "En cours";
            IconComponent = Clock;
          }

          return (
            <button
              type="button"
              key={name}
              aria-pressed={isSelected}
              onClick={() => onSelectMonth(monthNum)}
              className={`group relative flex min-h-[64px] flex-col justify-between rounded-xl border p-2.5 text-left transition-all duration-150 ${
                isSelected
                  ? "border-lenvia-blue bg-lenvia-blue/15 shadow-lg shadow-lenvia-blue/10 ring-1 ring-lenvia-blue/50"
                  : "border-white/10 bg-slate-950/40 hover:border-white/20 hover:bg-slate-950/70"
              }`}
            >
              <div className="flex items-center justify-between gap-1 w-full min-w-0">
                <span
                  title={name}
                  className={`text-xs font-bold uppercase tracking-wider truncate ${
                    isSelected ? "text-white" : "text-slate-300"
                  }`}
                >
                  {name}
                </span>
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span
                  className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-none ${badgeColor}`}
                >
                  <IconComponent className="h-2.5 w-2.5 flex-none" />
                  <span>{badgeText}</span>
                </span>
                {isConfigured && (
                  <span
                    className="inline-block text-[10px] text-slate-400"
                    title="Cibles saisies"
                  >
                    · Cibles saisies
                  </span>
                )}
              </div>

              {isSelected && (
                <div className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-lenvia-blue" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
